import { Image } from '@storybook/image';
import { useCallback, useEffect, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { PipelineMasterSteps, PipelineTemplateState } from 'global-stores';
import { useNotification } from 'hooks';
import { useDashboradhook } from 'views/routes-children';
import {
	SelectedCheckboxStepsState,
	SelectedStepsState,
	useOnboardingHook,
} from '../../store';
import { proofReadingKey } from 'views/pipelines/constants';
import { billedPrice } from 'views/complex-onboarding-flow/pricing/kyc-pricing';
import './builder-left-nav.scss';

// What the Basic Info step collects from the applicant. Mirrors BASIC_INFO_FIELDS
// in complex-onboarding-flow/components/basic-info-action, which draws the same
// two on the canvas node.
const BASIC_INFO_FIELDS = ['First Name', 'Last Name'];

// The entity types the KYB Documents step collects a document checklist for.
// Mirrors the `title` of each element in complex-onboarding-flow/components/
// kyb-form/form-data.ts, which is what the canvas node lists.
const KYB_ENTITY_TYPES = [
	'Corporation',
	'Organization',
	'Limited Liability Company',
	'Trust',
	'Estate',
	'Partnership',
	'Sole Proprietorship',
];

// The presence-only catalog entries: no price, nothing to configure. Kept in
// step with INCLUDED_STEP_IDS in complex-onboarding-flow/components/stat-strip,
// which counts the same four on the canvas.
const INCLUDED_STEP_KEYS = new Set([
	'basicInfo',
	'secondContact',
	'kybForm',
	'accountForm',
]);

interface IKYBMetaDataType {
	_id: string;
	key: string;
	type: string;
	default: string;
	description: string | string[];
	label: string;
	price?: number;
}

export const BuilderLeftNav = ({ onDragStart }: any) => {
	const templateInit = useRecoilValue(PipelineTemplateState);
	const selectedCheckbox = useRecoilValue(SelectedCheckboxStepsState);
	const steps = useRecoilValue(PipelineMasterSteps);
	const [selectedSteps, setSelectedSteps] = useRecoilState(SelectedStepsState);
	const { errorNotification } = useNotification();
	const { getPipelineInit, getTemplateInit } = useDashboradhook();
	const { isPricingHide } = useOnboardingHook();

	//constants
	const { proofVerification } = proofReadingKey;

	const globalActions = useMemo(() => {
		const stepActions: any[] = [];
		selectedSteps.forEach(({ actions }) => {
			stepActions.push(...actions);
		});
		return stepActions;
	}, [selectedSteps]);

	const handleSelectStep = useCallback(() => {
		const filtered = steps.filter(step => selectedCheckbox[step.key]);
		const isKycAvailable = filtered.findIndex(({ key }) => key === 'kyc') > -1;
		const isAmlAvailable = filtered.findIndex(({ key }) => key === 'aml') > -1;
		if (isAmlAvailable) {
			if (!isKycAvailable) {
				errorNotification('Oops! can not select AML without KYC');
				return;
			}
		}
		setSelectedSteps(filtered);
		// eslint-disable-next-line
	}, [selectedCheckbox, steps]);

	useEffect(() => {
		handleSelectStep();
		// eslint-disable-next-line
	}, [steps]);

	useEffect(() => {
		if (steps.length === 0) {
			getPipelineInit();
		} // eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (!templateInit) getTemplateInit();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	// here we are removing the face auth from the steps need to remove it if we again wanted to add the face auth as a step
	const navActionsWithoutAuth = useMemo(() => {
		// only for now proofVerification is hide for linear flow
		return globalActions.filter(
			action =>
				action.key !== 'authentication' &&
				action.key !== 'facialRecognition'
		);
	}, [globalActions]);

	// __simplici_kyc_aml_single_tile__: AML is an option ON the KYC card, not a
	// card of its own -- it cannot run without KYC, and handleSelectStep already
	// refuses that combination. Its price still comes from the catalog, so
	// /pipeline-init's per-business rate is what the row quotes.
	const amlAction = useMemo(
		() =>
			navActionsWithoutAuth.find(
				(action: any) => action?.key === 'amlVerification'
			),
		[navActionsWithoutAuth]
	);


	const getAllLists = useCallback(
		(_id: string, label: string, price: any, key: string, metadata: any) => {
			
			return (
				<div
					key={`${_id}_${key}`}
					className="BuilderLeftNav--list"
					onDragStart={(event: any) => onDragStart?.(event, key)}
					draggable
				>
					{/* __simplici_kyc_card_rows__: the catalog label for allId spells out
						the accepted documents; the card reads better named for the check
						itself. Scoped to this card so every other title still comes from
						the catalog. */}
					<div className="BuilderLeftNav--label">
						{/* __simplici_catalog_card_names__: two cards are named for the
							CHECK rather than for the documents the catalog lists.
							`allId` spells out every accepted document; `kybVerification`
							carries its own AML screening (Business Sanctions and OFAC is
							a row on the card), so it is "& AML" for the same reason the
							KYC card is. Scoped to these two keys so every other title
							still comes from the catalog. */}
						{key === 'allId'
							? 'KYC & AML'
							: key === 'kybVerification'
							? 'KYB & AML'
							: label}
					</div>
					{/* __simplici_included_chip__: basicInfo/secondContact/kybForm/accountForm
						are the same four presence-only catalog entries the stat strip counts
						under "Included steps" (INCLUDED_STEP_IDS in stat-strip/index.tsx) --
						no price, nothing to configure. A bare label (basicInfo/secondContact)
						or a "$0.00/Verification" row (kybForm/accountForm) both under-state
						that, so every one of the four gets the same "Included" chip instead. */}
					{/* Basic Info lists what it collects, one row per field, so the card
						reads like every other one: a named line with its cost on the
						right. There is no price, so "Included" takes that slot. */}
					{key === 'basicInfo' &&
						BASIC_INFO_FIELDS.map(field => (
							<div className="BuilderLeftNav--bottom" key={field}>
								<div className="BuilderLeftNav--key">{field}</div>
								<span className="BuilderLeftNav--included">Included</span>
							</div>
						))}
					{key === 'secondContact' && (
						<div className="BuilderLeftNav--bottom">
							<span className="BuilderLeftNav--included">
								Included
							</span>
						</div>
					)}
					{/* KYB Documents names the entity types it asks documents for, so the
						card says what the step covers rather than only that it is free. */}
					{key === 'kybForm' &&
						KYB_ENTITY_TYPES.map(entity => (
							<div className="BuilderLeftNav--bottom" key={entity}>
								<div className="BuilderLeftNav--key">{entity}</div>
								<span className="BuilderLeftNav--included">Included</span>
							</div>
						))}
					{key === 'successScreenCompletion' && (
						<>
							<div
								key={'successScreenCompletion'}
								className="BuilderLeftNav--bottom"
							>
								<div className="BuilderLeftNav--key">Success Screen</div>
								{isPricingHide && (
									<div className="BuilderLeftNav--price-field">
										<div className="BuilderLeftNav--price">
											${(price ?? 0).toFixed(2)}
										</div>
										<div className="BuilderLeftNav--slashes">/</div>
										<div className="BuilderLeftNav--verification">
											Verification
										</div>
									</div>
								)}
							</div>
						</>
					)}
					{key === 'allId' && (
						<>
							{/* __simplici_kyc_card_rows__: provider dropdowns are configuration,
								not chargeable checks. `kycProvider` is injected onto every
								verification step by PROVIDER_SELECTS (routes-children/hooks) and
								was being rendered here as a $0.00 line. Matched on shape --
								a `select` whose key ends in "Provider" -- rather than on the
								label, so it holds if the copy changes. Real toggles such as
								Liveliness are kept. */}
							{(metadata ?? [])
								.filter(
									(meta: any) =>
										!(
											meta?.type === 'select' &&
											/Provider$/.test(meta?.key ?? '')
										)
								)
								.map((meta: any) => (
									<div key={meta?.key} className="BuilderLeftNav--bottom">
										<div className="BuilderLeftNav--key">
											{meta?.label ?? 'KYC'}
										</div>
										{isPricingHide && (
											<div className="BuilderLeftNav--price-field">
												<div className="BuilderLeftNav--price">
													{/* The BILLED price, as the node card and Publish show it.
													    `meta.price` is the vendor figure on a vendor-priced
													    row, so the list quoted Global Data Validation at one
													    number and the tile at another. SSN stays free. */}
													${(meta?.key === 'ssnVerification' || meta?.key === 'ssn'
														? 0
														: billedPrice(meta)
													).toFixed(2)}
												</div>
												<div className="BuilderLeftNav--slashes">/</div>
												<div className="BuilderLeftNav--verification">
													Verification
												</div>
											</div>
										)}
									</div>
								))}
							{/* __simplici_kyc_aml_single_tile__: AML is part of this step, so it
								is listed here like the other checks. Whether it RUNS is chosen on
								the canvas tile -- the Actions List is a menu, not a control. */}
							{!!amlAction && (
								<div className="BuilderLeftNav--bottom">
									<div className="BuilderLeftNav--key">
										Global PEPs &amp; Sanctions
									</div>
									{isPricingHide && (
										<div className="BuilderLeftNav--price-field">
											<div className="BuilderLeftNav--price">
												${billedPrice(amlAction as any).toFixed(2)}
											</div>
											<div className="BuilderLeftNav--slashes">/</div>
											<div className="BuilderLeftNav--verification">
												Verification
											</div>
										</div>
									)}
								</div>
							)}
						</>
					)}
					{key === 'formAction' && (
						<div className="BuilderLeftNav--bottom">
							<div className="BuilderLeftNav--key">{label ?? 'Form'}</div>
							{isPricingHide && (
								<div className="BuilderLeftNav--price-field">
									<div className="BuilderLeftNav--price">
										${price?.toFixed(2)}
									</div>
									<div className="BuilderLeftNav--slashes">/</div>
									<div className="BuilderLeftNav--verification">
										Verification
									</div>
								</div>
							)}
						</div>
					)}

					{key === 'accountForm' && (
						<div className="BuilderLeftNav--bottom">
							<span className="BuilderLeftNav--included">
								Included
							</span>
						</div>
					)}
					{/* __simplici_aml_card_single_row__: this used to render
						metadata[0].options as the price list. For AML that is the injected
						`amlProvider` dropdown (PROVIDER_SELECTS in routes-children/hooks) --
						the AML action ships no metadata of its own, so the select lands at
						metadata[0]. Its options are {label, value} vendor choices with no
						price, so every row printed a bare "$" and presented a provider
						selection as if it were a chargeable line.

						The price lives on the action: /pipeline-init resolves
						amlVerification against the business's `aml` product, giving the real
						per-verification figure. Render that once. Provider selection is
						untouched -- the dropdown still exists in the step's config modal. */}
					{key === 'amlVerification' && (
						<div className="BuilderLeftNav--bottom">
							<div className="BuilderLeftNav--key">Global PEPs &amp; Sanctions</div>
							{isPricingHide && (
								<div className="BuilderLeftNav--price-field">
									<div className="BuilderLeftNav--price">
										${(price ?? 0).toFixed(2)}
									</div>
									<div className="BuilderLeftNav--slashes">/</div>
									<div className="BuilderLeftNav--verification">
										Verification
									</div>
								</div>
							)}
						</div>
					)}
					{key === proofVerification && (
						<div className="BuilderLeftNav--bottom">
							<div className="BuilderLeftNav--key">
								{label ?? 'amlVeridjaskjkdbjkabkbakjfication'}
							</div>
							{isPricingHide && (
								<div className="BuilderLeftNav--price-field">
									<div className="BuilderLeftNav--price">
										${price?.toFixed(2)}
									</div>
									<div className="BuilderLeftNav--slashes">/</div>
									<div className="BuilderLeftNav--verification">
										Verification
									</div>
								</div>
							)}
						</div>
					)}
					{key === 'kybVerification' && (
						<>
							{(metadata ?? []).map((meta: IKYBMetaDataType) => (
								<div key={meta?.key} className="BuilderLeftNav--bottom">
									<div className="BuilderLeftNav--key">
										{meta?.label ?? 'KYC'}
									</div>
									{isPricingHide && (
										<div className="BuilderLeftNav--price-field">
											<div className="BuilderLeftNav--price">
												${(meta?.price ?? 0).toFixed(2)}
											</div>
											<div className="BuilderLeftNav--slashes">/</div>
											<div className="BuilderLeftNav--verification">
												Verification
											</div>
										</div>
									)}
								</div>
							))}
						</>
					)}
					{key === 'accreditationVerify' && (
						<div className="BuilderLeftNav">
							{/* __simplici_accreditation_nav_range__: "Investor chooses" carries no
								price -- the level is not decided until the session runs -- and
								`undefined?.toFixed(2)` rendered a bare "$". A priceless option
								shows the range spanned by the options that DO have prices, the
								same figures the canvas tile quotes. */}
							{metadata.length > 0 &&
								metadata[0].options?.map((res: any) => {
									const priced = (metadata[0].options ?? [])
										.map((o: any) => Number(o?.price))
										.filter((n: number) => Number.isFinite(n) && n > 0);
									const hasOwnPrice = Number.isFinite(Number(res?.price));
									const lo = priced.length ? Math.min(...priced) : 0;
									const hi = priced.length ? Math.max(...priced) : 0;
									return (
									<div
										className="BuilderLeftNav--key-wrapper"
										key={`${res.label}`}
									>
										<div className="BuilderLeftNav--key">
											{res.label ?? 'accreditationVerify'}
										</div>
										{isPricingHide && (
											<div className="BuilderLeftNav--price-field">
												<div className="BuilderLeftNav--price">
													{hasOwnPrice
														? `$${Number(res.price).toFixed(2)}`
														: hi > lo
														? `$${lo.toFixed(2)} \u2013 $${hi.toFixed(2)}`
														: `$${lo.toFixed(2)}`}
												</div>
												<div className="BuilderLeftNav--slashes">/</div>
												<div className="BuilderLeftNav--verification">
													Verification
												</div>
											</div>
										)}
									</div>
									);
								})}
						</div>
					)}
					{key === 'signAgreementVerification' && (
						<>
							{(metadata?.[0]?.options ?? [{ label, price }]).map(
								(option: any, index: number) => (
									<div
										className="BuilderLeftNav--bottom"
										// eslint-disable-next-line react/no-array-index-key
										key={`${index}_${label}`}
									>
										<div className="BuilderLeftNav--key">
											{option?.label ?? label ?? 'signAgreementVerification'}
										</div>
										{isPricingHide && (
											<div className="BuilderLeftNav--price-field">
												<div className="BuilderLeftNav--price">
													${(option?.price ?? price)?.toFixed(2)}
												</div>
												<div className="BuilderLeftNav--slashes">/</div>
												{/* Arun Kumar : For Sign Aggrement verification changed into Packet */}
												<div className="BuilderLeftNav--verification">
													Packet
												</div>
											</div>
										)}
									</div>
								)
							)}
						</>
					)}
				</div>
			);
		},
		[
			isPricingHide,
			onDragStart,
			proofVerification,
			// __simplici_kyc_aml_single_tile__: without these the callback keeps the
			// values from the FIRST render -- when the catalog has not loaded, so
			// amlAction is undefined and the PEPs & Sanctions row never appears.
			amlAction,
		]
	);

	//Shahbaaz Need to replace after complex flow demo
	// This code will run only for complex flow
	useMemo(() => {
		const kybFormKey = {
			label: 'KYB Documents',
			description: 'Know Your Business Action',
			key: 'kybForm',
			price: 0,
			metadata: [],
			_id: 'kybForm',
			stepKey: 'kybForm',
		};
		const accountFormKey = {
			label: 'Individual or Business Questionnaire.',
			description: 'Individual or Business Questionnaire.',
			key: 'accountForm',
			price: 0,
			metadata: [],
			_id: 'accountForm',
			stepKey: 'accountForm',
		};
		navActionsWithoutAuth.push(kybFormKey, accountFormKey);
		// Basic Info (tier-0) — always draggable, pinned to the TOP of the list. Guard against a
		// duplicate when the backend catalog (master-pipelines) already surfaces it via a step.
		const basicInfoKey = {
			label: 'Basic Info',
			description: "Collects the applicant's basic identity (First and Last name).",
			key: 'basicInfo',
			price: 0,
			metadata: [],
			_id: 'basicInfo',
			stepKey: 'basicInfo',
		};
		if (!navActionsWithoutAuth.some((action: any) => action.key === 'basicInfo')) {
			navActionsWithoutAuth.unshift(basicInfoKey);
		}
		// Second Contact (tier-0 OTP gate) — always draggable. Sends + verifies the OTP for the
		// channel opposite the one onboarding started with; gates downstream nodes until verified.
		// unshift LAST so it lands ABOVE Basic Info (flow order: Second Contact → Basic Info).
		const secondContactKey = {
			label: 'Second Contact',
			description:
				"Verifies the applicant's second contact (the channel opposite onboarding's start) via OTP.",
			key: 'secondContact',
			price: 0,
			metadata: [],
			_id: 'secondContact',
			stepKey: 'secondContact',
		};
		if (!navActionsWithoutAuth.some((action: any) => action.key === 'secondContact')) {
			navActionsWithoutAuth.unshift(secondContactKey);
		}
	}, [navActionsWithoutAuth]);

	const renderMainComponent = useMemo(() => {
		// __simplici_kyc_aml_single_tile__: no standalone AML card -- it lives on
		// the KYC & AML card as a listed row, so there is only one way to add it.
		const visible = navActionsWithoutAuth.filter(
			(action: any) => action?.key !== 'amlVerification'
		);

		// The four presence-only steps -- the ones the card tags "Included" and
		// the stat strip counts under "Included steps" -- cost nothing and have
		// nothing to configure, so they are the least interesting thing to read
		// first. Everything that carries a price sorts above them; within each
		// group the catalog's own order is preserved (a stable partition, not a
		// sort, so /pipeline-init still decides the running order).
		const priced = visible.filter(
			(action: any) => !INCLUDED_STEP_KEYS.has(action?.key)
		);
		const included = visible.filter((action: any) =>
			INCLUDED_STEP_KEYS.has(action?.key)
		);

		// __simplici_catalog_kyb_after_kyc__: KYB sits directly under KYC & AML.
		// The two are the paired identity checks — one for the person, one for
		// the business — and the catalog's own order had five unrelated cards
		// between them. A stable move of one entry, so everything else keeps the
		// order /pipeline-init returned.
		const ordered = (() => {
			const kycAt = priced.findIndex((a: any) => a?.key === 'allId');
			const kybAt = priced.findIndex((a: any) => a?.key === 'kybVerification');
			if (kycAt < 0 || kybAt < 0 || kybAt === kycAt + 1) return priced;
			const out = [...priced];
			const [kyb] = out.splice(kybAt, 1);
			out.splice(out.findIndex((a: any) => a?.key === 'allId') + 1, 0, kyb);
			return out;
		})();

		return [...ordered, ...included].map(
			({ _id = '', label = '', key = '', price = 0.0, metadata = [] }) =>
				getAllLists(_id, label, price, key, metadata)
		);
	}, [getAllLists, navActionsWithoutAuth]);
	return (
		<div className="BuilderLeftNav--container">
			<div className="BuilderLeftNav--head">Actions List</div>
			<div className="BuilderLeftNav--desc">
				Drag and drop actions in onboarding flow.
			</div>

			{navActionsWithoutAuth.length === 0 ? (
				<Image fileName="ActionList.svg" className="illustration__emptyList" />
			) : (
				renderMainComponent
			)}
		</div>
	);
};
